import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { ContentLanguageService } from '../content-language.service';
import { SelectedOrganizationService } from '../institutions/selected-organization.service';

/**
 * Centro del que se toma el país para resolver el idioma.
 *
 * Para casi todos los roles hay un solo centro y esto es `orgs[0]`. El rol
 * Director puede tener varios, posiblemente de países distintos: ahí manda el
 * centro activo del selector (`SelectedOrganizationService`), no el primero
 * del perfil. Si lo seleccionado ya no pertenece al usuario —o todavía no
 * sembró nadie el selector— cae al primero, que es el comportamiento previo.
 */
export function pickActiveOrganization(
  organizations: any[] | null | undefined,
  selectedOrganizationId: number | null,
): any | undefined {
  const orgs = Array.isArray(organizations) ? organizations : [];
  if (selectedOrganizationId != null) {
    const selected = orgs.find((o) => Number(o?.organization) === selectedOrganizationId);
    if (selected) return selected;
  }
  return orgs[0];
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private userProfileCache = new Map<string, Observable<any>>();

  private currentUser$ = new BehaviorSubject<any>(null);

  constructor(
    private httpClient: HttpClient,
    private cookieService: CookieService,
    private contentLanguageService: ContentLanguageService,
    private selectedOrganization: SelectedOrganizationService
  ) { }

  getCurrentUser(): Observable<any> {
    return this.currentUser$.asObservable();
  }

  getCurrentUserValue(): any {
    return this.currentUser$.getValue();
  }

  setCurrentUser(user: any): void {
    this.currentUser$.next(user);
    // Persistimos el rol para que los layouts (ej. `layout-v3`) puedan elegir el
    // sidebar correcto de forma SÍNCRONA en el primer render, sin esperar al
    // perfil async (evita el "cambio" de sidenav al entrar a una pantalla v3).
    if (user?.user_type) {
      localStorage.setItem('gr_user_type', user.user_type);
    }
  }

  clearCurrentUser(): void {
    this.currentUser$.next(null);
    localStorage.removeItem('gr_user_type');
  }


  getRequestHeaders() {
    const options = {
      headers: {
        Authorization: 'Bearer ' + this.cookieService.get('gr_access')
      }
    }
    return options;
  }

  getUserProfileData(user_id: any): Observable<any> {
    const lang = this.contentLanguageService.getContentLang();
    const cacheKey = `${user_id}_${lang}`;

    if (!this.userProfileCache.has(cacheKey)) {
      const url = `${environment.apiUrl}/users/${user_id}/?lang=${lang}`;

      const request$ = this.httpClient.get(url).pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        tap((user: any) => {
          this.setCurrentUser(user);
          // El locale lo resuelve el backend y lo manda en
          // `users_organizations[].language_variants`. Se toma el del centro
          // ACTIVO: para el rol Director con centros en países distintos,
          // `orgs[0]` no es necesariamente el que está mirando.
          const activeOrg = pickActiveOrganization(
            user?.users_organizations,
            this.selectedOrganization.organizationId,
          );
          this.contentLanguageService.setContentLangFromOrganization(activeOrg);

          // El perfil se pidió con el idioma previo a conocer el país, así que
          // si la resolución lo cambió esta entrada quedó cacheada en el idioma
          // equivocado. Se descarta para que la próxima llamada vuelva a pedirlo
          // ya con el idioma correcto, en vez de servir contenido desactualizado
          // si el usuario vuelve a ese idioma más adelante.
          const resolved = this.contentLanguageService.getContentLang();
          if (resolved !== lang) {
            this.userProfileCache.delete(cacheKey);
          }
        }),
        catchError(error => {
          this.userProfileCache.delete(cacheKey);
          return throwError(() => error);
        })
      );

      this.userProfileCache.set(cacheKey, request$);
    }

    return this.userProfileCache.get(cacheKey)!;
  }

  clearUserProfileCache(user_id?: any): void {
    if (user_id) {
      this.userProfileCache.delete(user_id.toString());
    } else {
      this.userProfileCache.clear();
    }
  }

  geAlltUsers(): Observable<any> {
    const url = environment.apiUrl + '/users/';
    return this.httpClient.get(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    )
  }

  changeUserPassword(user_id: any, security_data: any): Observable<any> {
    const url = environment.apiUrl + '/users/change-password/' + user_id;
    return this.httpClient.put(url, security_data).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    )
  }

  editUserProfile(user_id: any, profile_data: any, profile_picture_file?: any): Observable<any> {
    const url = environment.apiUrl + '/users/profile/' + user_id;
    const formData = new FormData()

    if (!profile_data.tax_id) delete profile_data.tax_id;

    for (const key in profile_data) {
      formData.append(key, profile_data[key]);
    }

    formData.delete('image_profile');

    if (profile_picture_file) formData.append('image_profile', profile_picture_file);

    return this.httpClient.put(url, formData).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    )
  }

  getUserParametersV2(userId: number, questionaryId = 1): Observable<any> {
    // {{URL_GR}}/questionaries/3/user/2
    const URL = `${environment.apiUrl}/questionaries/${questionaryId}/user/${userId}`
    const handleErrors = catchError(error => throwError(() => error))

    return this.httpClient.get(URL).pipe(handleErrors)
  }

  getUserParameters(userId: number): Observable<any> {
    // {{URL_GR}}/questionaries/3/user/2
    const URL = `${environment.apiUrl}/users/${userId}/parameters`
    const handleErrors = catchError(error => throwError(() => error))

    return this.httpClient.get(URL).pipe(handleErrors)
  }

  getPreliminarReport(): Observable<Blob> {
    const URL = `${environment.apiUrl}/reports/preliminar-report`
    return this.httpClient.get(URL, { responseType: 'blob' })
  }

  getFinalReport(): Observable<Blob> {
    const URL = `${environment.apiUrl}/reports/final-report/`
    return this.httpClient.get(URL, { responseType: 'blob' })
  }

  getFinalReportByQuestionary(questionaryId: number): Observable<Blob> {
    const URL = `${environment.apiUrl}/reports/final-report/${questionaryId}`
    return this.httpClient.get(URL, { responseType: 'blob' })
  }

  getPreliminarPreview(): Observable<any> {
    const URL = `${environment.apiUrl}/reports/preliminar-preview`
    return this.httpClient.get(URL)
  }

  saveUserComment(studentId: number, orgId: number, roomId: number, commentData: { comment: string }): Observable<any> {
    const URL = `${environment.apiUrl}/reports/add-comment/${studentId}/${orgId}/${roomId}/`
    return this.httpClient.post(URL, commentData).pipe(
      catchError(error => throwError(() => error))
    )
  }

  fetchTeacherComment(
    studentId: number,
    orgId: number,
    roomId: number,
  ): Observable<{ id: number | null; comment: string; pdf_url: string | null }> {
    const URL = `${environment.apiUrl}/reports/teacher-comment/${studentId}/${orgId}/${roomId}/`;
    return this.httpClient
      .get<{ id: number | null; comment: string; pdf_url: string | null }>(URL)
      .pipe(catchError((error) => throwError(() => error)));
  }

  updateUserComment(studentId: number, orgId: number, roomId: number, commentData: { id: number; comment: string }): Observable<any> {
    const URL = `${environment.apiUrl}/reports/add-comment/${studentId}/${orgId}/${roomId}/`;
    return this.httpClient.patch(URL, commentData).pipe(
      catchError(error => throwError(() => error))
    );
  }
}
